"use client"

import { useState, useCallback } from "react"
import type { FieldMapping, ValidationResult } from "../types/field-mapping"
import { FIELD_MAPPING_CONSTANTS } from "../constants/field-mapping"

export const useFieldMapping = (initialMappings: FieldMapping[] = []) => {
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(initialMappings)

  const updateHubSpotField = useCallback((mappingId: string, newField: string) => {
    setFieldMappings((prev) =>
      prev.map((mapping) => (mapping.id === mappingId ? { ...mapping, hubspotField: newField } : mapping)),
    )
  }, [])

  const updateSalesforceField = useCallback((mappingId: string, newField: string) => {
    setFieldMappings((prev) =>
      prev.map((mapping) => (mapping.id === mappingId ? { ...mapping, salesforceField: newField } : mapping)),
    )
  }, [])

  const deleteMapping = useCallback((mappingId: string) => {
    setFieldMappings((prev) => prev.filter((mapping) => mapping.id !== mappingId))
  }, [])

  const addMapping = useCallback((hubspotFields: string[], salesforceFields: string[]) => {
    if (hubspotFields.length === 0 || salesforceFields.length === 0) {
      console.warn("Cannot add mapping: No fields available")
      return false
    }

    const newMapping: FieldMapping = {
      id: `${Date.now()}-${Math.random()}`,
      hubspotField: hubspotFields[0],
      salesforceField: salesforceFields[0],
    }

    setFieldMappings((prev) => [...prev, newMapping])
    return true
  }, [])

  const validateMappings = useCallback((): ValidationResult => {
    const errors: string[] = []

    if (fieldMappings.length < FIELD_MAPPING_CONSTANTS.MIN_MAPPINGS_REQUIRED) {
      errors.push(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.NO_MAPPINGS)
    }

    const invalidMappings = fieldMappings.filter((mapping) => !mapping.hubspotField || !mapping.salesforceField)

    if (invalidMappings.length > 0) {
      errors.push(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.INCOMPLETE_MAPPINGS)
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }, [fieldMappings])

  const saveMappings = useCallback(() => {
    try {
      localStorage.setItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.FIELD_MAPPINGS, JSON.stringify(fieldMappings))
      return true
    } catch (error) {
      console.error("Error saving field mappings:", error)
      return false
    }
  }, [fieldMappings])

  return {
    fieldMappings,
    setFieldMappings,
    updateHubSpotField,
    updateSalesforceField,
    deleteMapping,
    addMapping,
    validateMappings,
    saveMappings,
  }
}
